// Theme heading and paragraph rules lose to inline font-size, font-family, and color.
export const THEME_OVERRIDE_PROPS = ['font-size', 'font-family', 'color']

export function stripThemeOverrides(el) {
    if (!el || el.nodeType !== 1) return
    stripPropertiesFromTree(el, THEME_OVERRIDE_PROPS)
    unwrapBareSpans(el)
}

// Sets styles on a block so the whole paragraph changes, including text that
// already has the same value. Descendant copies of those properties are removed
// so they cannot hide the new value. This does not toggle a matching style off.
export function applyStylesToElement(el, styles) {
    if (!el || el.nodeType !== 1) return
    const props = Object.keys(styles || {})
    if (!props.length) return
    stripPropertiesFromTree(el, props, { skipRoot: true })
    Object.entries(styles).forEach(([name, value]) => {
        el.style.setProperty(name, value == null ? '' : String(value))
    })
    unwrapBareSpans(el)
}

export function applyTextStyle(app, styles) {
    const props = Object.keys(styles || {})
    if (!props.length || !app) return

    const selection = app.getService && app.getService('selection')
    const range = selection && selection.getRange && selection.getRange()
    if (!range) return

    const blocks = currentBlockElements(app)
    if (!blocks.length) return

    if (range.collapsed) {
        blocks.forEach((el) => applyStylesToElement(el, styles))
        return
    }

    blocks.forEach((el) => {
        const sub = intersectRangeWithElement(range, el)
        if (!sub) return
        if (rangeCoversElementContents(sub, el)) {
            applyStylesToElement(el, styles)
            return
        }
        const span = wrapRangeWithStyles(sub, styles)
        if (span && blocks.length === 1 && selection.select) selection.select(span)
    })
}

export function currentBlockElements(app) {
    const found = []
    const seen = new Set()
    const add = (el) => {
        if (!el || el.nodeType !== 1 || seen.has(el) || !el.isConnected) return
        seen.add(el)
        found.push(el)
    }

    const selection = app.getService && app.getService('selection')
    if (selection && selection.getNodes) {
        const nodes = selection.getNodes({ types: 'block' }) || []
        nodes.forEach(add)
    }

    const block = app.block && app.block.get && app.block.get()
    if (block && block.getEl) add(block.getEl())

    return found.filter((el) => !found.some((other) => other !== el && el.contains(other)))
}

export function wrapRangeWithStyles(range, styles) {
    const doc = range.startContainer.ownerDocument
    const props = Object.keys(styles || {})
    if (!doc || !props.length || range.collapsed) return null

    const span = doc.createElement('span')
    Object.entries(styles).forEach(([name, value]) => {
        span.style.setProperty(name, value == null ? '' : String(value))
    })

    try {
        range.surroundContents(span)
    } catch (e) {
        const fragment = range.extractContents()
        stripPropertiesFromTree(fragment, props)
        span.appendChild(fragment)
        range.insertNode(span)
    }

    stripPropertiesFromTree(span, props, { skipRoot: true })
    unwrapBareSpans(span)
    return span
}

function stripProperties(el, props) {
    if (!el || el.nodeType !== 1 || !el.style) return
    props.forEach((prop) => {
        if (el.style.getPropertyValue(prop)) el.style.removeProperty(prop)
    })
    if (!(el.getAttribute('style') || '').trim()) el.removeAttribute('style')
}

function stripPropertiesFromTree(root, props, { skipRoot = false } = {}) {
    if (!root) return
    if (!skipRoot && root.nodeType === 1) stripProperties(root, props)
    if (!root.querySelectorAll) return
    root.querySelectorAll('[style]').forEach((el) => {
        if (skipRoot && el === root) return
        stripProperties(el, props)
    })
}

function unwrapBareSpans(root) {
    if (!root || !root.querySelectorAll) return
    const spans = Array.from(root.querySelectorAll('span'))
    for (let i = spans.length - 1; i >= 0; i--) unwrapIfBare(spans[i])
}

function unwrapIfBare(el) {
    if (!el || el.tagName !== 'SPAN') return
    if ((el.getAttribute('style') || '').trim()) return
    if ((el.getAttribute('class') || '').trim()) return
    for (let i = 0; i < el.attributes.length; i++) {
        const name = el.attributes[i].name
        if (name !== 'style' && name !== 'class') return
    }
    const parent = el.parentNode
    if (!parent) return
    while (el.firstChild) parent.insertBefore(el.firstChild, el)
    parent.removeChild(el)
}

function rangeCoversElementContents(range, el) {
    const full = el.ownerDocument.createRange()
    full.selectNodeContents(el)
    return (
        range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
        range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
    )
}

function intersectRangeWithElement(range, el) {
    const full = el.ownerDocument.createRange()
    full.selectNodeContents(el)
    // START_TO_END compares this end to source start. END_TO_START compares this start to source end.
    if (range.compareBoundaryPoints(Range.START_TO_END, full) <= 0) return null
    if (range.compareBoundaryPoints(Range.END_TO_START, full) >= 0) return null

    const sub = range.cloneRange()
    if (range.compareBoundaryPoints(Range.START_TO_START, full) < 0) {
        sub.setStart(full.startContainer, full.startOffset)
    }
    if (range.compareBoundaryPoints(Range.END_TO_END, full) > 0) {
        sub.setEnd(full.endContainer, full.endOffset)
    }
    return sub.collapsed ? null : sub
}
